import type { JSX } from 'react';

// A dumb, reusable determinate progress bar for the merged Gateways stage
// (reveal-gateways-onetab follow-up). The width always reflects `value` (the bar
// is information, not decoration); only the easing between updates is motion-gated
// via CSS (disabled under prefers-reduced-motion). The honesty rules for the
// values live in lib/revealProgress.ts — this component just renders them.

export interface RevealProgressBarProps {
  /** Fill fraction in [0, 1]. */
  value: number;
  /** Accessible name for the bar (aria-label). */
  label: string;
  /** Stage-description line shown under the bar (announced politely). */
  description: string;
  /** Optional human progress text, e.g. "Step 2 of 3" (aria-valuetext). */
  valueText?: string;
  testId?: string;
}

export function RevealProgressBar({
  value,
  label,
  description,
  valueText,
  testId,
}: RevealProgressBarProps): JSX.Element {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div className="reveal-progress" data-testid={testId}>
      <div
        className="reveal-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={label}
        {...(valueText !== undefined ? { 'aria-valuetext': valueText } : {})}
      >
        <div className="reveal-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="reveal-progress__desc" aria-live="polite">
        {description}
      </p>
    </div>
  );
}
